import React, { useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Tag,
  Text,
  Tooltip,
  useBreakpointValue,
} from "@chakra-ui/react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import useAxios from "axios-hooks";
import { useSetRecoilState } from "recoil";
import { toastState } from "atoms/toastState";
import { AddIcon } from "@chakra-ui/icons";
import {
  MdSchedule,
  MdAccessTime,
  MdDeleteOutline,
  MdViewList,
  MdBarChart,
} from "react-icons/md";

// ── helpers ──────────────────────────────────────────────────────────────────

const DAYS = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];
const DAY_SHORT = {
  MONDAY: "Mon",
  TUESDAY: "Tue",
  WEDNESDAY: "Wed",
  THURSDAY: "Thu",
  FRIDAY: "Fri",
  SATURDAY: "Sat",
  SUNDAY: "Sun",
};
const PRICE_TYPES = ["FLAT", "ENERGY", "TIME", "PARKING_TIME"];

const VISIBLE_PRICE_TYPES = ["ENERGY"];

function timeToMinutes(t) {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

function windowsOverlap(aStart, aEnd, bStart, bEnd) {
  const contains = (s, e, t) => {
    const sm = timeToMinutes(s),
      em = timeToMinutes(e),
      tm = timeToMinutes(t);
    if (sm <= em) return tm >= sm && tm <= em;
    return tm >= sm || tm <= em;
  };
  return (
    contains(aStart, aEnd, bStart) ||
    contains(aStart, aEnd, bEnd) ||
    contains(bStart, bEnd, aStart) ||
    contains(bStart, bEnd, aEnd)
  );
}

function detectOverlap(elements) {
  for (let i = 0; i < elements.length; i++) {
    for (let j = i + 1; j < elements.length; j++) {
      const a = elements[i],
        b = elements[j];
      const sharedDay = a.days.some((d) => b.days.includes(d));
      if (
        sharedDay &&
        windowsOverlap(a.startTime, a.endTime, b.startTime, b.endTime)
      ) {
        return `Time slot ${i + 1} and time slot ${j + 1} overlap on the same day(s). Please adjust the times.`;
      }
    }
  }
  return null;
}

function elementFromModel(el) {
  if (!el.Restriction) return null;
  const r = el.Restriction;
  if (!r.StartTime || !r.EndTime || !r.DayOfWeek?.length) return null;
  const prices = {};
  (el.PriceComponents || []).forEach((pc) => {
    prices[pc.Type] = pc.Price;
  });
  return {
    startTime: r.StartTime,
    endTime: r.EndTime,
    days: r.DayOfWeek,
    prices: {
      FLAT: prices.FLAT ?? 0,
      ENERGY: prices.ENERGY ?? 0,
      TIME: prices.TIME ?? 0,
      PARKING_TIME: prices.PARKING_TIME ?? 0,
    },
  };
}

// ── sub-components ────────────────────────────────────────────────────────────

const glassStyle = {
  backgroundColor: "rgba(255,255,255,0.6)",
  backdropFilter: "blur(12px)",
  border: "1px solid rgba(245,125,54,0.15)",
  boxShadow: "0 8px 32px 0 rgba(31,38,135,0.07)",
};

function DayChip({ day, selected, onClick }) {
  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      px={2}
      py={1}
      borderRadius="6px"
      fontSize="11px"
      fontWeight="600"
      cursor="pointer"
      border="1px solid"
      borderColor={selected ? "brand.500" : "rgba(245,125,54,0.35)"}
      bg={selected ? "brand.500" : "transparent"}
      color={selected ? "white" : "brand.500"}
      transition="all 0.15s"
      _hover={{ bg: selected ? "#ff5c00" : "rgba(245,125,54,0.1)" }}
    >
      {DAY_SHORT[day]}
    </Box>
  );
}

function ElementCard({ el, index, onRemove }) {
  return (
    <Box aspectRatio="1 / 1" w="100%" maxW="200px">
      <Box
        h="100%"
        p={3}
        borderRadius="6px"
        border="1px solid rgba(245,125,54,0.15)"
        borderLeft="4px solid #23A979"
        bg="rgba(245,125,54,0.03)"
        position="relative"
        transition="all 0.2s ease"
        _hover={{
          boxShadow: "0 4px 12px rgba(245,125,54,0.1)",
          borderColor: "rgba(245,125,54,0.3)",
        }}
      >
        {/* Delete button pinned top-right */}
        <Tooltip label="Remove slot">
          <IconButton
            icon={<MdDeleteOutline />}
            size="sm"
            variant="ghost"
            color="red.400"
            aria-label="Remove slot"
            position="absolute"
            top={1}
            right={1}
            onClick={() => onRemove(index)}
            _hover={{ bg: "red.50", color: "red.600" }}
          />
        </Tooltip>

        <Flex direction="column" justify="space-between" h="100%">
          {/* Time range with clock icon */}
          <Flex align="center" gap={2} mb={2} pr={8}>
            <Icon as={MdAccessTime} color="brand.500" boxSize={4} />
            <Text fontSize="sm" fontWeight="700" color="brand.500" noOfLines={1}>
              {el.startTime} – {el.endTime}
            </Text>
          </Flex>

          {/* Active days */}
          <Flex
            gap={1.5}
            flexWrap="wrap"
            flex={1}
            alignContent="flex-start"
            mb={3}
          >
            {el.days.map((d) => (
              <Tag
                key={d}
                size="sm"
                bg="rgba(245,125,54,0.12)"
                color="brand.500"
                borderRadius="4px"
                fontSize="11px"
                fontWeight="600"
                px={2}
                py={0.5}
              >
                {DAY_SHORT[d]}
              </Tag>
            ))}
          </Flex>

          {/* Rate */}
          <Flex align="center" gap={2}>
            <Text fontSize="xs" color="gray.500" fontWeight="600">
              Rate:
            </Text>
            <Tag
              size="sm"
              bg="rgba(245,125,54,0.15)"
              color="brand.500"
              borderRadius="4px"
              fontSize="xs"
              fontWeight="700"
              px={2}
            >
              ₹ {el.prices["ENERGY"]}/kWh
            </Tag>
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
}

const CHART_COLORS = [
  "#4ECDC4",
  "#45B7D1",
  "#96CEB4",
  "#FFEAA7",
  "#DDA0DD",
  "#98D8C8",
  "#F7DC6F",
  "#BB8FCE",
];

function TariffChart({ elements, defaultPrice, onRemove }) {
  const [removeIdx, setRemoveIdx] = useState(null);
  const cancelRef = useRef();
  const gridCount = useBreakpointValue({ base: 7, md: 25 }) ?? 25;

  const getColor = (i) => CHART_COLORS[i % CHART_COLORS.length];

  const renderDayRow = (day, dayIndex) => {
    const dayEls = elements
      .map((el, idx) => ({ ...el, originalIndex: idx }))
      .filter((el) => el.days.includes(day));

    return (
      <Flex
        key={day}
        position="relative"
        h={{ base: "36px", md: "44px" }}
        align="center"
      >
        <Text
          position="absolute"
          left={0}
          w={{ base: "36px", md: "50px" }}
          fontSize={{ base: "10px", md: "xs" }}
          fontWeight="600"
          color="gray.600"
        >
          {DAY_SHORT[day]}
        </Text>
        <Box
          position="relative"
          flex={1}
          h="100%"
          ml={{ base: "36px", md: "50px" }}
        >
          {/* Hour grid lines */}
          {[...Array(gridCount)].map((_, idx) => {
            const h = gridCount === 25 ? idx : idx * 4;
            return (
              <Box
                key={h}
                position="absolute"
                left={`${(h / 24) * 100}%`}
                top={0}
                bottom={0}
                w="1px"
                bg="rgba(0,0,0,0.04)"
              />
            );
          })}

          {/* Default rate background */}
          <Tooltip
            label={
              <Box>
                <Text fontWeight="700" fontSize="sm">
                  Default Rate
                </Text>
                <Text fontSize="xs" mt={1}>
                  ENERGY: ₹ {defaultPrice}/kWh
                </Text>
                <Text fontSize="xs" color="gray.400">
                  Applied when no custom slot matches
                </Text>
              </Box>
            }
            placement="top"
            hasArrow
            bg="white"
            color="gray.700"
            border="1px solid rgba(245,125,54,0.2)"
            boxShadow="0 4px 12px rgba(0,0,0,0.1)"
            borderRadius="8px"
            p={2}
          >
            <Box
              as="div"
              display="block"
              position="absolute"
              left={0}
              top="6px"
              bottom="6px"
              right={0}
              bg="rgba(245,125,54,0.18)"
              borderRadius="6px"
              border="2px solid rgba(245,125,54,0.5)"
              cursor="default"
            />
          </Tooltip>

          {/* Element blocks */}
          {dayEls.map((el) => {
            const startM = timeToMinutes(el.startTime);
            const endM = timeToMinutes(el.endTime);
            let left, width;
            if (endM > startM) {
              left = (startM / 1440) * 100;
              width = ((endM - startM) / 1440) * 100;
            } else {
              // overnight: show as two blocks
              left = (startM / 1440) * 100;
              width = ((1440 - startM) / 1440) * 100;
            }
            const color = getColor(el.originalIndex);

            return (
              <Tooltip
                key={el.originalIndex}
                label={
                  <Box>
                    <Text fontWeight="700" fontSize="sm">
                      {el.startTime} – {el.endTime}
                    </Text>
                    <Text fontSize="xs" mt={1}>
                      FLAT: ₹ {el.prices.FLAT}
                    </Text>
                    <Text fontSize="xs">ENERGY: ₹ {el.prices.ENERGY}/kWh</Text>
                    <Text fontSize="xs">TIME: ₹ {el.prices.TIME}</Text>
                    <Text fontSize="xs">
                      PARKING: ₹ {el.prices.PARKING_TIME}
                    </Text>
                    <Text fontSize="10px" color="gray.400" mt={1}>
                      Click to remove
                    </Text>
                  </Box>
                }
                placement="top"
                hasArrow
                bg="white"
                color="gray.700"
                border="1px solid rgba(245,125,54,0.2)"
                boxShadow="0 4px 12px rgba(0,0,0,0.1)"
                borderRadius="8px"
                p={2}
              >
                <Box
                  position="absolute"
                  left={`${left}%`}
                  width={`${width}%`}
                  top="6px"
                  bottom="6px"
                  bg={color}
                  borderRadius="6px"
                  cursor="pointer"
                  opacity={0.9}
                  border="1px solid rgba(255,255,255,0.4)"
                  _hover={{
                    opacity: 1,
                    transform: "scaleY(1.08)",
                    zIndex: 10,
                  }}
                  transition="all 0.15s ease"
                  onClick={() => setRemoveIdx(el.originalIndex)}
                />
              </Tooltip>
            );
          })}
        </Box>
      </Flex>
    );
  };

  return (
    <Box>
      <Box pb={2}>
        <Box>
          {DAYS.map((day, i) => renderDayRow(day, i))}
          {/* Time axis labels */}
          <Flex
            ml={{ base: "36px", md: "50px" }}
            mt={2}
            position="relative"
            h="16px"
          >
            {[0, 4, 8, 12, 16, 20, 24].map((h) => (
              <Text
                key={h}
                position="absolute"
                left={`${(h / 24) * 100}%`}
                transform="translateX(-50%)"
                fontSize={{ base: "9px", md: "10px" }}
                color="gray.400"
              >
                {String(h).padStart(2, "0")}:00
              </Text>
            ))}
          </Flex>
        </Box>
      </Box>

      {/* Legend */}
      <Flex gap={{ base: 2, md: 3 }} mt={4} flexWrap="wrap" align="center">
        <Flex align="center" gap={2}>
          <Box
            w={{ base: 3, md: 4 }}
            h={{ base: 3, md: 4 }}
            borderRadius="4px"
            bg="rgba(245,125,54,0.1)"
            border="1px dashed rgba(245,125,54,0.3)"
          />
          <Text
            fontSize={{ base: "10px", md: "xs" }}
            color="gray.600"
            fontWeight="500"
          >
            Default Rate: ₹ {defaultPrice}/kWh
          </Text>
        </Flex>
        {elements.map((el, i) => (
          <Flex key={i} align="flex-start" gap={2}>
            <Box
              w={{ base: 3, md: 4 }}
              h={{ base: 3, md: 4 }}
              borderRadius="4px"
              bg={getColor(i)}
              border="1px solid rgba(0,0,0,0.05)"
              flexShrink={0}
              mt="2px"
            />
            <Box>
              <Text
                fontSize={{ base: "10px", md: "xs" }}
                color="gray.600"
                fontWeight="600"
                mb={0.5}
              >
                Slot {i + 1}: {el.startTime}–{el.endTime}
              </Text>
              <Text fontSize={{ base: "9px", md: "10px" }} color="gray.500">
                FLAT: ₹ {el.prices.FLAT}
              </Text>
              <Text fontSize={{ base: "9px", md: "10px" }} color="gray.500">
                ENERGY: ₹ {el.prices.ENERGY}/kWh
              </Text>
              <Text fontSize={{ base: "9px", md: "10px" }} color="gray.500">
                TIME: ₹ {el.prices.TIME}
              </Text>
              <Text fontSize={{ base: "9px", md: "10px" }} color="gray.500">
                PARKING: ₹ {el.prices.PARKING_TIME}
              </Text>
            </Box>
          </Flex>
        ))}
      </Flex>

      {/* Remove confirmation dialog */}
      <AlertDialog
        isOpen={removeIdx !== null}
        leastDestructiveRef={cancelRef}
        onClose={() => setRemoveIdx(null)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="12px">
            <AlertDialogHeader fontSize="md" fontWeight="700">
              Remove Time Slot?
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to remove this time slot?
            </AlertDialogBody>
            <AlertDialogFooter gap={2}>
              <Button
                ref={cancelRef}
                onClick={() => setRemoveIdx(null)}
                variant="ghost"
                size="sm"
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={() => {
                  if (removeIdx !== null) onRemove(removeIdx);
                  setRemoveIdx(null);
                }}
                size="sm"
              >
                Remove
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}

const EMPTY_DRAFT = {
  startTime: "",
  endTime: "",
  days: [],
  prices: { FLAT: 0, ENERGY: 0, TIME: 0, PARKING_TIME: 0 },
};

function AddElementForm({ onAdd, onCancel, existingElements }) {
  const [draft, setDraft] = useState({
    ...EMPTY_DRAFT,
    prices: { ...EMPTY_DRAFT.prices },
  });
  const [localError, setLocalError] = useState("");
  const [endTimeError, setEndTimeError] = useState("");

  const toggleDay = (day) => {
    setDraft((prev) => ({
      ...prev,
      days: prev.days.includes(day)
        ? prev.days.filter((d) => d !== day)
        : [...prev.days, day],
    }));
  };

  const selectDays = (dayList) => {
    setDraft((prev) => ({
      ...prev,
      days: dayList,
    }));
  };

  const setPrice = (type, val) => {
    setDraft((prev) => ({
      ...prev,
      prices: { ...prev.prices, [type]: parseFloat(val) || 0 },
    }));
  };

  const isValid =
    draft.startTime !== "" &&
    draft.endTime !== "" &&
    draft.endTime > draft.startTime &&
    draft.days.length > 0;

  const handleAdd = () => {
    setLocalError("");
    const overlap = detectOverlap([...existingElements, draft]);
    if (overlap) {
      setLocalError(overlap);
      return;
    }
    onAdd(draft);
  };

  return (
    <Box>
      <Text fontSize="xs" color="gray.500" mb={4}>
        Set a different energy rate for a specific time window and days.
      </Text>

      {/* Time window */}
      <Flex gap={3} mb={4} align="flex-start" flexWrap="wrap">
        <FormControl w="auto">
          <FormLabel fontSize="sm" fontWeight="700" color="gray.700" mb={1}>
            From
          </FormLabel>
          <Input
            type="time"
            size="md"
            h="44px"
            w="160px"
            borderRadius="4px"
            bg="white"
            fontSize="sm"
            fontWeight="600"
            color="gray.800"
            value={draft.startTime}
            onChange={(e) => {
              const val = e.target.value;
              setDraft((p) => ({
                ...p,
                startTime: val,
                endTime: p.endTime && p.endTime <= val ? "" : p.endTime,
              }));
            }}
            focusBorderColor="brand.500"
            border="2px solid rgba(245,125,54,0.4)"
            _hover={{ borderColor: "brand.500" }}
            _focus={{ borderColor: "brand.500", boxShadow: "0 0 0 1px #23A979" }}
          />
          {/* Reserve same space as error text so arrow stays aligned */}
          <Box h="18px" />
        </FormControl>
        <Text fontSize="sm" color="gray.500" mt="30px" fontWeight="700">
          →
        </Text>
        <FormControl w="auto">
          <FormLabel fontSize="sm" fontWeight="700" color="gray.700" mb={1}>
            To
          </FormLabel>
          <Input
            type="time"
            size="md"
            h="44px"
            w="160px"
            borderRadius="4px"
            bg="white"
            fontSize="sm"
            fontWeight="600"
            color="gray.800"
            value={draft.endTime}
            min={draft.startTime}
            onChange={(e) => {
              const val = e.target.value;
              setDraft((p) => ({ ...p, endTime: val }));
              if (val && draft.startTime && val <= draft.startTime) {
                setEndTimeError("End time must be after start time");
              } else {
                setEndTimeError("");
              }
            }}
            isDisabled={!draft.startTime}
            focusBorderColor="brand.500"
            border={
              endTimeError ? "2px solid" : "2px solid rgba(245,125,54,0.4)"
            }
            borderColor={endTimeError ? "red.400" : undefined}
            _hover={{ borderColor: endTimeError ? "red.400" : "brand.500" }}
            _focus={{
              borderColor: endTimeError ? "red.400" : "brand.500",
              boxShadow: endTimeError
                ? "0 0 0 1px #FC8181"
                : "0 0 0 1px #23A979",
            }}
          />
          {endTimeError && (
            <Text fontSize="xs" color="red.400" mt={1}>
              {endTimeError}
            </Text>
          )}
        </FormControl>
      </Flex>

      {/* Active Days */}
      <Box mb={4}>
        <Flex justify="space-between" align="center" mb={2}>
          <FormLabel fontSize="xs" fontWeight="600" color="gray.600" m={0}>
            Active Days{" "}
            <Text as="span" color="red.400">
              *
            </Text>
          </FormLabel>
          <Flex gap={2}>
            <Button
              size="xs"
              variant="ghost"
              color="brand.500"
              fontSize="11px"
              fontWeight="600"
              px={2}
              py={1}
              h="auto"
              onClick={() =>
                selectDays([
                  "MONDAY",
                  "TUESDAY",
                  "WEDNESDAY",
                  "THURSDAY",
                  "FRIDAY",
                ])
              }
            >
              Weekdays
            </Button>
            <Button
              size="xs"
              variant="ghost"
              color="brand.500"
              fontSize="11px"
              fontWeight="600"
              px={2}
              py={1}
              h="auto"
              onClick={() => selectDays(["SATURDAY", "SUNDAY"])}
            >
              Weekend
            </Button>
            <Button
              size="xs"
              variant="ghost"
              color="gray.500"
              fontSize="11px"
              fontWeight="600"
              px={2}
              py={1}
              h="auto"
              onClick={() => selectDays([])}
            >
              Clear
            </Button>
          </Flex>
        </Flex>
        <Flex gap={1.5} flexWrap="wrap">
          {DAYS.map((d) => (
            <DayChip
              key={d}
              day={d}
              selected={draft.days.includes(d)}
              onClick={() => toggleDay(d)}
            />
          ))}
        </Flex>
      </Box>

      {/* Energy Rate */}
      <Box mb={4}>
        <FormControl maxW="200px">
          <FormLabel fontSize="xs" fontWeight="600" color="gray.600" mb={1}>
            Energy Rate (₹/kWh)
          </FormLabel>
          <Input
            type="number"
            size="sm"
            h="40px"
            borderRadius="8px"
            value={draft.prices["ENERGY"]}
            onChange={(e) => setPrice("ENERGY", e.target.value)}
            focusBorderColor="brand.500"
            border="1px solid rgba(245,125,54,0.2)"
            min={0}
            step={0.01}
            placeholder="0.00"
          />
        </FormControl>
      </Box>

      {localError && (
        <Text fontSize="xs" color="red.500" mb={3}>
          {localError}
        </Text>
      )}

      <Flex gap={3}>
        <Button
          size="sm"
          bg="brand.500"
          color="white"
          fontWeight="600"
          _hover={{ bg: "#ff5c00" }}
          isDisabled={!isValid}
          onClick={handleAdd}
        >
          Add Time Slot
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </Flex>
    </Box>
  );
}

// ── main component ────────────────────────────────────────────────────────────

const PublicBilling = ({ id, station, refresh }) => {
  const setToast = useSetRecoilState(toastState);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState("chart"); // "list" | "chart"
  const [overrideElements, setOverrideElements] = useState([]);
  const [overlapError, setOverlapError] = useState("");

  const [{ loading }, executeTariff] = useAxios(
    { method: "PUT", url: `/api/stations/tariff/${id}` },
    { manual: true },
  );

  const priceSchema = yup.object().shape({
    Price: yup
      .number()
      .min(0, "Must be a positive number or zero")
      .required("Price is required")
      .typeError("Must be a number"),
  });

  const {
    control,
    formState: { errors, isValid },
    setValue,
    getValues,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(priceSchema),
    defaultValues: { Price: station?.Tariffs?.[0]?.Price ?? 0 },
  });

  const activeTariffId = station?.Tariffs?.[0]?.Id;

  useEffect(() => {
    setValue("Price", station?.Tariffs?.[0]?.Price ?? 0);
    const elements = (station?.Tariffs?.[0]?.Elements ?? [])
      .map(elementFromModel)
      .filter(Boolean);
    setOverrideElements(elements);
  }, [activeTariffId, setValue]); // key off tariff ID — changes on every new version

  const buildPayload = (price) => ({
    currency: "INR",
    price: parseFloat(price),
    elements: overrideElements.map((el, i) => ({
      element_order: i,
      price_components: PRICE_TYPES.map((t) => ({
        type: t,
        price: el.prices[t],
        step_size: 1,
      })),
      restriction: {
        start_time: el.startTime,
        end_time: el.endTime,
        day_of_week: el.days,
      },
    })),
  });

  const saveFull = async () => {
    setOverlapError("");
    const overlap = detectOverlap(overrideElements);
    if (overlap) {
      setOverlapError(overlap);
      return;
    }
    const price = getValues("Price");
    try {
      await executeTariff({ data: buildPayload(price) });
      setToast({
        isOpen: true,
        title: "Tariff updated successfully",
        status: "success",
        position: "top-right",
        duration: 3000,
      });
      await refresh();
    } catch (err) {
      const e = err?.response?.data?.metadata;
      setToast({
        isOpen: true,
        title: e?.error_code ?? "Error occurred",
        description: e?.error_message ?? null,
        status: "error",
        position: "top-right",
        duration: 5000,
      });
    }
  };

  const removeElement = (idx) => {
    setOverrideElements((prev) => prev.filter((_, i) => i !== idx));
    setOverlapError("");
  };

  const addElement = (el) => {
    setOverrideElements((prev) => [...prev, el]);
    setIsModalOpen(false);
  };

  return (
    <Box mt={5} p={4} borderRadius="12px" {...glassStyle}>
      {/* Title */}
      <Flex align="center" gap={2} mb={4}>
        <Icon as={MdSchedule} color="brand.500" boxSize={5} />
        <Text fontSize="md" fontWeight="bold" color="gray.700">
          Charging Price
        </Text>
      </Flex>

      {/* Default price row */}
      <Flex gap={3} align="flex-end" flexWrap="wrap">
        {/* Price input */}
        <Controller
          name="Price"
          control={control}
          render={({ field }) => (
            <FormControl isInvalid={!!errors.Price} maxW="180px">
              <FormLabel fontSize="xs" fontWeight="600" color="gray.600" mb={1}>
                Default Rate (₹/kWh)
              </FormLabel>
              <Input
                {...field}
                type="number"
                step="0.01"
                placeholder="0.00"
                focusBorderColor="brand.500"
                bg="white"
                size="sm"
                borderRadius="4px"
                border="1px solid rgba(245,125,54,0.2)"
                _hover={{ borderColor: "brand.500" }}
                _focus={{ boxShadow: "0 0 0 3px rgba(245,125,54,0.1)" }}
              />
              {errors.Price && (
                <FormErrorMessage fontSize="xs" mt={1}>
                  {errors.Price.message}
                </FormErrorMessage>
              )}
            </FormControl>
          )}
        />

        {/* Fixed GST badge */}
        <Box mb={1}>
          <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>
            GST
          </Text>
          <Tag
            size="sm"
            bg="rgba(245,125,54,0.12)"
            color="brand.500"
            fontWeight="700"
            borderRadius="4px"
            px={3}
            py={2}
            fontSize="xs"
          >
            18%
          </Tag>
        </Box>
      </Flex>

      {/* Custom Pricing Schedule */}
      <Divider my={4} borderColor="rgba(245,125,54,0.2)" />

      {/* Section header */}
      <Flex
        justify="space-between"
        align={{ base: "flex-start", md: "center" }}
        mb={4}
        gap={3}
        direction={{ base: "column", md: "row" }}
      >
        <Box>
          <Flex align="center" gap={2} mb={1}>
            <Icon as={MdAccessTime} color="brand.500" boxSize={4} />
            <Text fontSize="sm" fontWeight="700" color="gray.700">
              Pricing Schedule
            </Text>
          </Flex>
          <Text fontSize="xs" color="gray.500">
            Define different energy rates for different times of the day.
          </Text>
        </Box>

        <Flex gap={2} align="center">
          {/* View toggle */}
          <Flex
            bg="rgba(255,255,255,0.6)"
            backdropFilter="blur(8px)"
            border="1px solid rgba(245,125,54,0.15)"
            borderRadius="8px"
            p="2px"
          >
            <Button
              size="xs"
              leftIcon={<Icon as={MdBarChart} boxSize={3.5} />}
              bg={viewMode === "chart" ? "brand.500" : "transparent"}
              color={viewMode === "chart" ? "white" : "brand.500"}
              _hover={{
                bg: viewMode === "chart" ? "#e86e20" : "rgba(245,125,54,0.08)",
              }}
              borderRadius="4px"
              fontWeight="600"
              px={3}
              py={1.5}
              h="auto"
              onClick={() => setViewMode("chart")}
            >
              Chart
            </Button>
            <Button
              size="xs"
              leftIcon={<Icon as={MdViewList} boxSize={3.5} />}
              bg={viewMode === "list" ? "brand.500" : "transparent"}
              color={viewMode === "list" ? "white" : "brand.500"}
              _hover={{
                bg: viewMode === "list" ? "#e86e20" : "rgba(245,125,54,0.08)",
              }}
              borderRadius="4px"
              fontWeight="600"
              px={3}
              py={1.5}
              h="auto"
              onClick={() => setViewMode("list")}
            >
              List
            </Button>
          </Flex>

          <Button
            size="sm"
            bg="brand.500"
            color="white"
            fontWeight="600"
            borderRadius="4px"
            _hover={{ bg: "#ff5c00" }}
            leftIcon={<AddIcon boxSize={3} />}
            onClick={() => setIsModalOpen(true)}
          >
            Add Time Slot
          </Button>
        </Flex>
      </Flex>

      {/* Chart view */}
      {viewMode === "chart" && (
        <Box
          p={{ base: 2, md: 4 }}
          borderRadius="12px"
          border="1px solid rgba(245,125,54,0.15)"
          bg="rgba(255,255,255,0.4)"
          mb={3}
        >
          <TariffChart
            elements={overrideElements}
            defaultPrice={getValues("Price") ?? 0}
            onRemove={removeElement}
          />
        </Box>
      )}

      {/* List view */}
      {viewMode === "list" && (
        <Grid
          templateColumns={{
            base: "1fr",
            md: "1fr 1fr",
            lg: "1fr 1fr 1fr",
            xl: "1fr 1fr 1fr 1fr",
            "2xl": "1fr 1fr 1fr 1fr 1fr",
          }}
          gap={4}
          mb={3}
        >
          {overrideElements.map((el, i) => (
            <ElementCard key={i} el={el} index={i} onRemove={removeElement} />
          ))}
        </Grid>
      )}

      {overlapError && (
        <Text fontSize="xs" color="red.500" mt={2}>
          {overlapError}
        </Text>
      )}

      <Flex justify="flex-end" mt={4}>
        <Button
          size="sm"
          bg="brand.500"
          color="white"
          borderRadius="4px"
          _hover={{ bg: "#ff5c00" }}
          isLoading={loading}
          isDisabled={!isValid}
          onClick={saveFull}
        >
          Save Tariff
        </Button>
      </Flex>

      {/* Add Element Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        size="lg"
        isCentered
      >
        <ModalOverlay bg="rgba(0,0,0,0.3)" backdropFilter="blur(4px)" />
        <ModalContent
          borderRadius="8px"
          bg="white"
          boxShadow="0 8px 32px rgba(0,0,0,0.12)"
        >
          <ModalHeader fontSize="md" fontWeight="700" color="gray.700">
            Add New Time Slot
          </ModalHeader>
          <ModalCloseButton color="gray.500" />
          <ModalBody pb={4}>
            <AddElementForm
              existingElements={overrideElements}
              onAdd={addElement}
              onCancel={() => setIsModalOpen(false)}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PublicBilling;
